import { createPortal } from "react-dom";
import { useState } from "react";
import IC_BACK from "@/assets/app/back_arrow.svg?react";
import IC_CLOSE_WIN from "@/assets/common/ic_collect_close.svg?react";
import { sendCaptchas, userLogout, userDestroy, ESendCaptchasType } from "@/services/api_user";
import useChecBeforeExit from "@/layout/checkBeforeExit";
import { setLoginSize } from "@/utils/window";
import { useNavigate } from "react-router-dom";
import Checkbox from "@/component/Checkbox";
import { message } from "antd";
import userInfoStore from "@/store/user.store";
import IcRecordFinishLoading from "@/assets/images/ic_record_finish_loading.png";
import { IS_PRIVATE } from '@/config';
import ModifyPassword from './ModifyPassword';
import PwdChangedModal from './PwdChangedModal';

interface IProp {
  onClose: () => void;
}

let countInterval: NodeJS.Timeout | number | undefined = undefined;

// 账号管理
export default function AccountManager(prop: IProp) {
  const navigate = useNavigate();
  const mobile = userInfoStore((state) => state.mobile);

  const [showDeregist, setShowDeregist] = useState(false);
  const [showModifyPassword, setShowModifyPassword] = useState(false);
  const [showPasswordSuccessModal, setShowPasswordSuccessModal] = useState(false);

  const [step, setStep] = useState<number>(1);
  const [isAgree, setIsAgree] = useState(false);
  const [canClick, setCanClick] = useState(false);
  const [clickLoading, setClickLoading] = useState(false);

  // 验证码相关
  const [captchasSending, setSaptchasSending] = useState(false);
  const [captchas, setCaptchas] = useState("");
  const [countDown, setCountDown] = useState(60);

  const backHandle = () => {
    setShowDeregist(false);
    setIsAgree(false);
    setStep(1);
    setCanClick(false);
    setCaptchas("");
    setClickLoading(false);
  }

  const closeHandle = () => {
    prop.onClose();
  }

  const logout = useChecBeforeExit(async () => {
    const res = await userLogout();
    if (res) {
      setLoginSize();
      navigate("/login", { replace: true });
    } else {
      message.warning("退出登录失败");
    }
  });

  const toDestroy = () => {
    setShowDeregist(true);
  }

  const toModifyPassword = () => {
    setShowModifyPassword(true);
  }

  const checkClick = (isAgree: boolean, content?: string) => {
    if (step == 1) {
      setCanClick(isAgree);
    } else {
      if (isAgree) {
        const isCaptchasValid = content ? content.length == 6 : false;
        setCanClick(isCaptchasValid);
        return;
      } else {
        setCanClick(false);
        return;
      }
    }
  }

  const sendHandle = async () => {
    if (captchasSending) return;
    const data = await sendCaptchas(mobile, ESendCaptchasType.Destroy);
    if (!data.success) {
      message.warning("验证码发送失败"); 
      if (data.data && !isNaN(Number(data.data))) {
        setCountDown(Number(data.data));
      } else {
        return;
      }
    }
    setSaptchasSending(true);
    countInterval = setInterval(() => {
      setCountDown((count) => {
        if (count === 1) {
          setSaptchasSending(false);
          clearInterval(countInterval);
          countInterval = undefined;
          return 60;
        } else {
          return count - 1;
        }
      });
    }, 1000);

  };

  const onCaptchasInput = (content: string) => {
    const value = content.replace(/\D/gm, "");
    setCaptchas(value);
    checkClick(isAgree, value);
  };

  const destroyHandle = useChecBeforeExit(async () => {
    const res = await userDestroy(captchas);
    setClickLoading(false);
    if (res) {
      setLoginSize();
      navigate("/login", { replace: true });
    } else {
      message.warning("用户注销失败");
    }
  });

  const getDisplayMobile = (mobile: string) => {
    if (!mobile) return "";
    return mobile.replace(/(\d{3})\d{4}(\d{4})/, "$1****$2");
  }

  // 如果显示修改密码组件，则渲染它
  if (showModifyPassword) {
    return (
      <ModifyPassword
        onClose={(success?: boolean) => {
          setShowModifyPassword(false);
          if (success) {
            setShowPasswordSuccessModal(true);
          }
        }}
      />
    );
  }

  if (showPasswordSuccessModal) {
    return <PwdChangedModal visible={showPasswordSuccessModal} onClose={() => setShowPasswordSuccessModal(false)} />;
  }

  return createPortal(
    <div className="fixed top-0 w-full h-full bg-black bg-opacity-50">
      <div className="absolute flex flex-col w-105 h-106 rd-3 left-1/2 top-1/2 -translate-1/2 bg-white overflow-hidden">
        {showDeregist && <IC_BACK className="absolute w-3 h-3 flex-self-start cursor-pointer m-4.5 z-99"
          onClick={backHandle}>
        </IC_BACK>}
        <IC_CLOSE_WIN className="absolute w-6 h-6 flex-self-end cursor-pointer m-3 z-99"
          onClick={closeHandle}>
        </IC_CLOSE_WIN>
        <div className="w-full flex justify-center items-center select-none h-12 border-0 border-solid border-#ECEEF3 border-b-0.25">
          <span className={`text-size-4 text-#30343C`}>{showDeregist ? "账号注销" : "账号设置"}</span>
        </div>

        {showDeregist ?
          <div className="flex flex-col flex-1 justify-between select-none">
            {step == 1 ?
              <div className="m-8 flex flex-col">
                <span className="text-#626773 text-size-3 mb-6">
                  为了保障您的账号安全，请仔细阅读以下内容：
                </span>
                <span className="text-#30343C text-size-4 mb-2">
                  1. 永久注销，无法登录
                </span>
                <span className="text-#626773 text-size-3.5 mb-6">
                  账号一旦注销，您将无法使用本账号再次登录AI会记。
                </span>
                <span className="text-#30343C text-size-4 mb-2">
                  2. 所有数据将被删除，无法找回
                </span>
                <span className="text-#626773 text-size-3.5 mb-6 lh-6">
                  账号一旦注销，您的全部个人信息、个人内容、数据将被永久删除，无法找回。
                </span>
              </div> :
              <div className="flex flex-col justify-center items-center flex-1 px-8">
                <span className="text-#30343C text-size-4 mb-8 lh-8 whitespace-pre-wrap text-wrap text-align-center">
                  {`已发送验证码至注册手机${getDisplayMobile(mobile)} \n请输码进行身份验证`}
                </span>
                <div className="flex flex-justify-between bg-#F4F6F9 rd-2.5 w-full box-border">
                  <div className="flex-1 h-12 py-1 px-3">
                    <input
                      className="h-10 border-0 bg-transparent text-size-4 focus:outline-none placeholder:text-#A1A7B2 placeholder:text-size-4"
                      placeholder="6位短信验证码"
                      maxLength={6}
                      onChange={(e) => {
                        onCaptchasInput(e.target.value);
                      }}
                    ></input>
                  </div>
                  <div className={`px-4 h-12 ${captchasSending ? "text-#A1A7B2" : "text-#4981F2 cursor-pointer"} text-size-3.5 ml-2 lh-12`} onClick={sendHandle}>
                    {captchasSending ? `${countDown}s` : "获取验证码"}
                  </div>
                </div>
              </div>
            }
            <div className="w-full h-14 b-t-1 b-t-solid b-t-#ECEEF3 flex px-4 items-center justify-between">
              <Checkbox
                checked={isAgree}
                onChange={() => {
                  setIsAgree(!isAgree);
                  checkClick(!isAgree);
                }}
              >
                <span className="text-size-3.5 text-#A1A7B2">我已阅读并同意注销账号</span>
              </Checkbox>
              <button
                disabled={!canClick || clickLoading}
                className={`w-22 h-8 rd-1.5 text-white text-size-3.5 outline-none border-none cursor-pointer ${canClick ? "bg-#FF584C hover:opacity-90" : "bg-#FFD4C9"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (step == 1) {
                    setStep(2);
                    setCanClick(false);
                  } else {
                    setClickLoading(true);
                    destroyHandle();
                  }
                }}
              >
                {clickLoading ? <img src={IcRecordFinishLoading} className="rotation size-4" /> : (step == 1 ? "下一步" : "确认注销")}
              </button>
            </div>
          </div>
          :
          <div className="my-4">
            {/* 修改密码选项 */}
            {IS_PRIVATE && (
              <div className="w-full h-22.8 flex items-center justify-between px-8 py-6 select-none">
                <div className="flex flex-col justify-between h-full">
                  <span className="text-#30343C font-size-4">修改密码</span>
                  <span className="text-#A1A7B2 font-size-3">重新设置账号的密码</span>
                </div>
                <div className="w-14 h-8 b-1 b-solid b-#DBDEE4 rd-1 text-align-center lh-8 text-mx-black text-size-3 cursor-pointer" onClick={toModifyPassword}>前往</div>
              </div>
            )}
            {/* 账号退出选项 */}
            <div className="w-full h-22.8 flex items-center justify-between px-8 py-6 select-none">
              <div className="flex flex-col justify-between h-full">
                <span className="text-#30343C font-size-4">账号退出</span>
                <span className="text-#A1A7B2 font-size-3">安全退出当前账号</span>
              </div>
              <div className="w-14 h-8 b-1 b-solid b-#DBDEE4 rd-1 text-align-center lh-8 text-mx-black text-size-3 cursor-pointer" onClick={logout}>退出</div>
            </div>
            {!IS_PRIVATE && (
              <div className="w-full h-22.8 flex items-center justify-between px-8 py-6 select-none">
                <div className="flex flex-col justify-between h-full">
                  <span className="text-#30343C font-size-4">账号注销</span>
                  <span className="text-#A1A7B2 font-size-3">注销后将永久删除账户及所有文件，请谨慎操作</span>
                </div>
                <div className="w-14 h-8 b-1 b-solid b-#DBDEE4 rd-1 text-align-center lh-8 text-mx-black text-size-3 cursor-pointer" onClick={toDestroy}>前往</div>
              </div>
            )}
          </div>}
      </div>
    </div>,
    document.body
  );
}
